import { HierarchyLevel, SalesHierarchyNode, SalesDataPoint } from "../types"
import { max_store_count } from "./aggregation"
import { type CustomerKindRecord, customer_type_is_ship_to } from "./customer_kind"
import { metrics_part_cases } from "./quantity_basis"
import { round_cases } from "./quantity_basis"

/**
 * Ship-to (by distributor) view: regroup a retailer-level plan under the distributor(s) each
 * customer flows through, using the `metadata.rtm.distributors` allocations the Confido RTM
 * mappings import stores on customer nodes (e.g. NCG = UNFI East 0.6 / UNFI West 0.4).
 *
 * Purely presentational: nodes are scaled clones with synthetic ids, and saves always post the
 * raw hierarchy, so nothing here ever writes back.
 */

interface RtmDistributorShare {
  name: string
  allocation: number
}

export const UNMAPPED_DISTRIBUTOR = "No distributor mapping"

/**
 * Distributor per customer name (lower-cased), for customers whose node carries no
 * `metadata.rtm` split. Built from the brand's own account list by {@link ship_to_fallback_from_customers}.
 */
export type ShipToFallback = Map<string, string>

/**
 * The distributor each account buys through, read from the brand's customer records: a retailer
 * with a `parent_customer_id` goes through its parent; a distributor or direct account is its own
 * ship-to. Untyped, parentless accounts are left out (they stay unmapped).
 *
 * This is what the Ship-to view falls back to when a customer node has no `metadata.rtm`: a
 * customer added as a plain row (or before the dialog wrote the split) showed "No distributor
 * mapping" even though its record says it goes through UNFI (1st Phorm: Whole Foods → UNFI
 * (SuperValu), Sprouts → KeHE, both unmapped on the 2027 test plan).
 */
export function ship_to_fallback_from_customers(customers: CustomerKindRecord[]): ShipToFallback {
  const by_id = new Map<string, CustomerKindRecord>(customers.map((c: CustomerKindRecord) => [c.id, c]))
  const out: ShipToFallback = new Map()
  for (const customer of customers) {
    const key = customer.name.trim().toLowerCase()
    if (!key) continue
    const parent = customer.parent_customer_id ? by_id.get(customer.parent_customer_id) : undefined
    if (parent && parent.name.trim()) {
      out.set(key, parent.name.trim())
    } else if (customer_type_is_ship_to(customer.customer_type)) {
      out.set(key, customer.name.trim())
    }
  }
  return out
}

/** The customer-record name a forecast node is matched on: the node's name, else its `rtm.retailer`. */
function fallback_lookup_names(node: SalesHierarchyNode): string[] {
  const names = [node.name]
  const retailer = (node.metadata as any)?.rtm?.retailer
  if (typeof retailer === "string" && retailer.trim()) names.push(retailer)
  return names.map((n: string) => n.trim().toLowerCase()).filter((n: string) => n.length > 0)
}

export function distributor_shares(node: SalesHierarchyNode, fallback?: ShipToFallback): RtmDistributorShare[] {
  const raw = (node.metadata as any)?.rtm?.distributors
  const shares = Array.isArray(raw)
    ? raw
        .map((entry: any) => ({
          name: String(entry?.name || "").trim() || UNMAPPED_DISTRIBUTOR,
          allocation: Number(entry?.allocation),
        }))
        .filter((share: RtmDistributorShare) => Number.isFinite(share.allocation) && share.allocation > 0)
    : []
  if (shares.length > 0) return shares
  if (fallback) {
    for (const name of fallback_lookup_names(node)) {
      const distributor = fallback.get(name)
      if (distributor) return [{ name: distributor, allocation: 1 }]
    }
  }
  return [{ name: UNMAPPED_DISTRIBUTOR, allocation: 1 }]
}

function scale_sales_data(sales_data: SalesDataPoint[] | undefined, share: number): SalesDataPoint[] {
  if (!Array.isArray(sales_data)) return []
  if (share === 1) return sales_data.map((dp: SalesDataPoint) => structuredClone(dp))
  return sales_data.map((dp: SalesDataPoint) => {
    const clone = structuredClone(dp)
    clone.metrics = {
      ...clone.metrics,
      volume_dollars: Number(clone.metrics.volume_dollars || 0) * share,
      cases_sold: round_cases(Number(clone.metrics.cases_sold || 0) * share),
      store_count: Math.round(Number(clone.metrics.store_count || 0) * share),
      // The velocity denominator scales with the split like the doors do — the spread above
      // would otherwise carry the full unscaled distribution into every split clone.
      ...(clone.metrics.sku_store_count != null
        ? { sku_store_count: Math.round(Number(clone.metrics.sku_store_count) * share) }
        : {}),
      // A collected part-case sum splits with the volume; a leaf's own is implied from the
      // scaled dollars above.
      ...(clone.metrics.part_cases != null ? { part_cases: Number(clone.metrics.part_cases) * share } : {}),
      ...(clone.metrics.units_sold != null
        ? { units_sold: Math.round(Number(clone.metrics.units_sold) * share) }
        : {}),
    }
    return clone
  })
}

function scale_subtree(
  node: SalesHierarchyNode,
  share: number,
  id_prefix: string,
  parent_id: string | undefined
): SalesHierarchyNode {
  const scaled_id = `${id_prefix}-${node.id}`
  return {
    ...(node as any),
    id: scaled_id,
    parent_id,
    sales_data: scale_sales_data(node.sales_data, share),
    children: Array.isArray(node.children)
      ? (node.children as SalesHierarchyNode[]).map((child: SalesHierarchyNode) =>
          scale_subtree(child, share, id_prefix, scaled_id)
        )
      : node.children,
  } as SalesHierarchyNode
}

/** Sum children's points per period onto a parent (same additive shape as the grid rollup). */
function sum_children_sales_data(children: SalesHierarchyNode[]): SalesDataPoint[] {
  const by_period = new Map<string, SalesDataPoint>()
  for (const child of children) {
    for (const dp of child.sales_data || []) {
      const key = dp.period.period_key
      const existing = by_period.get(key)
      if (!existing) {
        by_period.set(key, structuredClone(dp))
        continue
      }
      existing.metrics = {
        ...existing.metrics,
        volume_dollars: Number(existing.metrics.volume_dollars || 0) + Number(dp.metrics.volume_dollars || 0),
        cases_sold: Number(existing.metrics.cases_sold || 0) + Number(dp.metrics.cases_sold || 0),
        part_cases: metrics_part_cases(existing.metrics) + metrics_part_cases(dp.metrics),
        store_count: max_store_count(existing.metrics.store_count, dp.metrics.store_count),
        // Distribution points sum across children (each side falls back to its doors when it
        // is a leaf) — keeping only the first child's value made root velocity divide summed
        // units by one child's count.
        sku_store_count:
          Number(existing.metrics.sku_store_count ?? existing.metrics.store_count ?? 0) +
          Number(dp.metrics.sku_store_count ?? dp.metrics.store_count ?? 0),
        ...(existing.metrics.units_sold != null || dp.metrics.units_sold != null
          ? { units_sold: Number(existing.metrics.units_sold || 0) + Number(dp.metrics.units_sold || 0) }
          : {}),
      }
    }
  }
  return [...by_period.values()].sort((a, b) => a.period.period_key.localeCompare(b.period.period_key))
}

function total_dollars(node: SalesHierarchyNode): number {
  return (node.sales_data || []).reduce(
    (sum: number, dp: SalesDataPoint) => sum + Number(dp.metrics?.volume_dollars || 0),
    0
  )
}

export function aggregateByShipToDistributor(nodes: SalesHierarchyNode[], fallback?: ShipToFallback): SalesHierarchyNode[] {
  const distributor_children = new Map<string, SalesHierarchyNode[]>()

  for (const customer of nodes) {
    for (const share of distributor_shares(customer, fallback)) {
      const slug = share.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")
      const distributor_id = `shipto-${slug}`
      const scaled = scale_subtree(customer, share.allocation, `${distributor_id}`, distributor_id)
      // A split customer shows its share of the total in each distributor's branch.
      if (share.allocation !== 1) {
        scaled.name = `${customer.name} (${Math.round(share.allocation * 100)}%)`
      }
      const list = distributor_children.get(share.name) ?? []
      list.push(scaled)
      distributor_children.set(share.name, list)
    }
  }

  const roots: SalesHierarchyNode[] = [...distributor_children.entries()].map(([name, children]) => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-")
    children.sort((a: SalesHierarchyNode, b: SalesHierarchyNode) => total_dollars(b) - total_dollars(a))
    return {
      id: `shipto-${slug}`,
      name,
      level: HierarchyLevel.DISTRIBUTOR,
      parent_id: undefined,
      metadata: { synthetic_ship_to_group: true },
      sales_data: sum_children_sales_data(children),
      children,
      is_expanded: false,
      is_visible: true,
    } as unknown as SalesHierarchyNode
  })

  roots.sort((a: SalesHierarchyNode, b: SalesHierarchyNode) => total_dollars(b) - total_dollars(a))
  return roots
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  const point = (period_key: string, dollars: number, cases: number, stores: number): SalesDataPoint =>
    ({
      period: { year: 2027, month: Number(period_key.slice(5)), period_key },
      metrics: { volume_dollars: dollars, cases_sold: cases, store_count: stores, case_cost: 0 },
      is_forecast: true,
    }) as any

  const customer = (
    id: string,
    name: string,
    distributors: { name: string; allocation: number | string }[] | null,
    dollars: number
  ): SalesHierarchyNode =>
    ({
      id,
      name,
      level: HierarchyLevel.CUSTOMER,
      metadata: distributors ? { rtm: { distributors } } : undefined,
      sales_data: [point("2027-01", dollars, dollars / 10, 100)],
      children: [
        {
          id: `${id}-line`,
          name: "Sparkling Water - MP",
          level: HierarchyLevel.PRODUCT_LINE,
          parent_id: id,
          sales_data: [point("2027-01", dollars, dollars / 10, 100)],
          children: [],
        },
      ],
    }) as any

  describe("ship_to_fallback_from_customers", () => {
    const records: CustomerKindRecord[] = [
      { id: "u", name: "UNFI (SuperValu)", customer_type: "Direct Distributor" },
      { id: "k", name: "KeHE", customer_type: "Distributor" },
      { id: "kr", name: "Kroger", customer_type: "Direct" },
      { id: "wf", name: "Whole Foods", parent_customer_id: "u" },
      { id: "sp", name: "Sprouts", parent_customer_id: "k" },
      { id: "7", name: "7-Eleven", customer_type: null, parent_customer_id: null },
    ]

    it("maps a retailer to its parent and a distributor or direct account to itself", () => {
      const fallback = ship_to_fallback_from_customers(records)
      expect(fallback.get("whole foods")).toBe("UNFI (SuperValu)")
      expect(fallback.get("sprouts")).toBe("KeHE")
      expect(fallback.get("kroger")).toBe("Kroger")
      expect(fallback.get("kehe")).toBe("KeHE")
      expect(fallback.has("7-eleven")).toBe(false)
    })

    it("groups a customer with no rtm metadata under the distributor its record names", () => {
      const fallback = ship_to_fallback_from_customers(records)
      const roots = aggregateByShipToDistributor(
        [customer("wf", "Whole Foods", null, 1000), customer("sp", "Sprouts", null, 500), customer("7", "7-Eleven", null, 100)],
        fallback
      )
      const by_name = new Map(roots.map((r: SalesHierarchyNode) => [r.name, r]))
      const child_names = (name: string): string[] =>
        ((by_name.get(name)?.children ?? []) as SalesHierarchyNode[]).map((c: SalesHierarchyNode) => c.name)
      expect(child_names("UNFI (SuperValu)")).toEqual(["Whole Foods"])
      expect(child_names("KeHE")).toEqual(["Sprouts"])
      expect(child_names(UNMAPPED_DISTRIBUTOR)).toEqual(["7-Eleven"])
    })

    it("never overrides an explicit rtm split, and does nothing without a fallback", () => {
      const fallback = ship_to_fallback_from_customers(records)
      const split = aggregateByShipToDistributor([customer("wf", "Whole Foods", [{ name: "UNFI East", allocation: 1 }], 1000)], fallback)
      expect(split.map((r: SalesHierarchyNode) => r.name)).toEqual(["UNFI East"])
      const bare = aggregateByShipToDistributor([customer("wf", "Whole Foods", null, 1000)])
      expect(bare.map((r: SalesHierarchyNode) => r.name)).toEqual([UNMAPPED_DISTRIBUTOR])
    })
  })

  describe("aggregateByShipToDistributor sku_store_count", () => {
    const point_with_sku = (period_key: string, stores: number, sku_stores: number): SalesDataPoint =>
      ({
        period: { year: 2027, month: 1, period_key },
        metrics: {
          volume_dollars: 1000,
          cases_sold: 100,
          store_count: stores,
          sku_store_count: sku_stores,
          case_cost: 0,
        },
        is_forecast: true,
      }) as any

    const sku_customer = (id: string, share: number, stores: number, sku_stores: number): SalesHierarchyNode =>
      ({
        id,
        name: id,
        level: HierarchyLevel.CUSTOMER,
        metadata: { rtm: { distributors: [{ name: "KeHE", allocation: share }, { name: "UNFI", allocation: 1 - share }] } },
        sales_data: [point_with_sku("2027-01", stores, sku_stores)],
        children: [],
      }) as any

    it("scales the velocity denominator with the split share, like doors", () => {
      const roots = aggregateByShipToDistributor([sku_customer("wf", 0.6, 100, 400)])
      const kehe = roots.find((r: any) => r.name.includes("KeHE")) as any
      const kehe_wf = kehe.children[0]
      expect(kehe_wf.sales_data[0].metrics.store_count).toBe(60)
      // 400 distribution points × 60% — not the full 400 the metrics spread used to carry.
      expect(kehe_wf.sales_data[0].metrics.sku_store_count).toBe(240)
    })

    it("sums the denominator across a distributor's customers instead of keeping the first", () => {
      const roots = aggregateByShipToDistributor([
        sku_customer("wf", 1, 100, 400),
        sku_customer("sprouts", 1, 50, 300),
      ])
      const kehe = roots.find((r: any) => r.name === "KeHE") as any
      expect(kehe.sales_data[0].metrics.sku_store_count).toBe(700)
    })
  })

  describe("aggregateByShipToDistributor", () => {
    it("groups customers under their distributor and rolls the totals up", () => {
      const roots = aggregateByShipToDistributor([
        customer("publix", "Publix", [{ name: "KeHE", allocation: 1 }], 1000),
        customer("sprouts", "Sprouts", [{ name: "KeHE", allocation: "1.0" }], 500),
        customer("wegmans", "Wegmans", [{ name: "UNFI East", allocation: 1 }], 200),
      ])

      expect(roots.map((r: SalesHierarchyNode) => r.name)).toEqual(["KeHE", "UNFI East"])
      const kehe = roots[0]
      expect(kehe.level).toBe(HierarchyLevel.DISTRIBUTOR)
      expect(kehe.children!.map((c: any) => c.name)).toEqual(["Publix", "Sprouts"])
      expect(kehe.sales_data[0].metrics.volume_dollars).toBe(1500)
      // Ids are re-namespaced per distributor branch so expansion state can't collide.
      expect((kehe.children![0] as any).id).toBe("shipto-kehe-publix")
      expect((kehe.children![0] as any).children[0].parent_id).toBe("shipto-kehe-publix")
    })

    it("reports the widest customer's doors under a distributor, not their sum", () => {
      // Two retailers behind KeHE, 100 doors each. Volume adds; doors do not stack the way
      // dollars do, so the distributor row reports the widest reach (see `max_store_count`).
      const roots = aggregateByShipToDistributor([
        customer("publix", "Publix", [{ name: "KeHE", allocation: 1 }], 1000),
        customer("sprouts", "Sprouts", [{ name: "KeHE", allocation: 1 }], 500),
      ])

      expect(roots[0].sales_data[0].metrics.store_count).toBe(100)
      expect(roots[0].sales_data[0].metrics.volume_dollars).toBe(1500)
    })

    it("splits an allocated customer across distributors with scaled metrics", () => {
      const roots = aggregateByShipToDistributor([
        customer(
          "ncg",
          "NCG",
          [
            { name: "UNFI East", allocation: "0.6" },
            { name: "UNFI West", allocation: "0.4" },
          ],
          1000
        ),
      ])

      const east = roots.find((r: SalesHierarchyNode) => r.name === "UNFI East")!
      const west = roots.find((r: SalesHierarchyNode) => r.name === "UNFI West")!
      expect(east.sales_data[0].metrics.volume_dollars).toBeCloseTo(600)
      expect(west.sales_data[0].metrics.volume_dollars).toBeCloseTo(400)
      expect((east.children![0] as any).name).toBe("NCG (60%)")
      // The split's leaf rows scale too, so drill-down matches the roll-up.
      expect((east.children![0] as any).children[0].sales_data[0].metrics.volume_dollars).toBeCloseTo(600)
      // Together the splits reproduce the original total.
      const total =
        east.sales_data[0].metrics.volume_dollars + west.sales_data[0].metrics.volume_dollars
      expect(total).toBeCloseTo(1000)
    })

    it("buckets customers with no RTM mapping instead of dropping them", () => {
      const roots = aggregateByShipToDistributor([customer("faire", "Faire", null, 50)])
      expect(roots[0].name).toBe("No distributor mapping")
      expect(roots[0].sales_data[0].metrics.volume_dollars).toBe(50)
    })
  })
}
